import { motion } from "framer-motion";
import { useEffect, useRef, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { clearSelectedDoctor, getDoctorNameList, getDoctorById } from '../../redux/doctorSlice';
import { useTranslation } from 'react-i18next';
import WelcomeSection from "../../components/dashboard/WelcomeSection";
import EarningsAnalytics from "../../components/dashboard/EarningsAnalytics";
import AppointmentAnalytics from "../../components/dashboard/AppointmentAnalytics";
import EarnYearGraph from "../../components/dashboard/EarnYearGraph";
import AppointmentYearGraph from "../../components/dashboard/AppointmentYearGraph";
import DashboardFilters from "../../components/dashboard/DashboardFilters";
import KpiCards from "../../components/dashboard/KpiCards";
import UpcomingAppointmentsTable from "../../components/dashboard/UpcomingAppointmentsTable";
import SpecialtyTypeBreakdown from "../../components/dashboard/SpecialtyTypeBreakdown";
import OccupancyByDay from "../../components/dashboard/OccupancyByDay";
import NoShowAnalytics from "../../components/dashboard/NoShowAnalytics";
import SpecialtyTrendGraph from "../../components/dashboard/SpecialtyTrendGraph";
import DashboardTabCard from "../../components/dashboard/DashboardTabCard";
import CommonLoader from '../../components/CommonLoader';
import ErrorPage from '../../components/ErrorPage';
import CustomDatePicker from '../../components/dashboard/CustomDatePicker';
import { useDashboardSocket } from '../../hooks/useDashboardSocket';
import { ChevronDown, ChevronUp, Search, Calendar, Clock, CalendarDays, CalendarRange, Settings2 } from 'lucide-react';

// Debounce Hook
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
};

function DoctorDashboard() {
  const { t } = useTranslation('dashboard');
  const { doctorNameList, selectedDoctor, loading,loadingList, error } = useSelector((state) => state.doctor);
  const userRole = localStorage.getItem('Role');
  const dispatch = useDispatch();

  // Dashboard period state
  const [dashboardPeriod, setDashboardPeriod] = useState('week');
  const [customStartDate, setCustomStartDate] = useState(null);
  const [customEndDate, setCustomEndDate] = useState(null);

  // Dashboard cross-cutting filters (Dashboard spec) — impact the whole dashboard
  const [selectedSpecialtyId, setSelectedSpecialtyId] = useState(null);
  const [selectedAddress, setSelectedAddress] = useState(null);

  // Controls lifted out of child components so the whole doctor snapshot is
  // driven by a single socket subscription (the children now just render these
  // and call the setters).
  const [apptYear, setApptYear] = useState(new Date().getFullYear());   // AppointmentYearGraph
  const [earnYear, setEarnYear] = useState(new Date().getFullYear());   // EarnYearGraph
  const [upcomingPage, setUpcomingPage] = useState(1);                  // UpcomingAppointmentsTable

  // Reset custom dates when switching away from custom
  const handlePeriodChange = (period) => {
    if (period !== 'custom') {
      setCustomStartDate(null);
      setCustomEndDate(null);
    }
    setDashboardPeriod(period);
  };

  const getDateRange = (rangeType, customStart, customEnd) => {
    const today = new Date();
    let startDate, endDate;
    switch (rangeType) {
      case 'today':
        startDate = new Date(today); startDate.setHours(0, 0, 0, 0);
        endDate = new Date(today); endDate.setHours(23, 59, 59, 999);
        break;
      case 'week': {
        const currentDay = today.getDay();
        const daysToMonday = currentDay === 0 ? -6 : 1 - currentDay;
        startDate = new Date(today); startDate.setDate(today.getDate() + daysToMonday); startDate.setHours(0, 0, 0, 0);
        endDate = new Date(startDate); endDate.setDate(startDate.getDate() + 6); endDate.setHours(23, 59, 59, 999);
        break;
      }
      case 'month':
        startDate = new Date(today.getFullYear(), today.getMonth(), 1); startDate.setHours(0, 0, 0, 0);
        endDate = new Date(today.getFullYear(), today.getMonth() + 1, 0); endDate.setHours(23, 59, 59, 999);
        break;
      case 'custom':
        if (customStart && customEnd) {
          startDate = new Date(customStart); startDate.setHours(0, 0, 0, 0);
          endDate = new Date(customEnd); endDate.setHours(23, 59, 59, 999);
        }
        break;
      default:
        startDate = new Date(today); startDate.setHours(0, 0, 0, 0);
        endDate = new Date(today); endDate.setHours(23, 59, 59, 999);
    }
    if (!startDate || !endDate) return { startDate: null, endDate: null };
    return { startDate: startDate.toISOString(), endDate: endDate.toISOString() };
  };

  const dashboardDateRange = dashboardPeriod === 'custom'
    ? getDateRange('custom', customStartDate, customEndDate)
    : getDateRange(dashboardPeriod);

  // For upcoming appointments: always start from today, end at period end
  const getUpcomingDateRange = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayISO = today.toISOString();

    if (dashboardPeriod === 'today') {
      return dashboardDateRange;
    }
    if (dashboardPeriod === 'custom') {
      if (!customStartDate || !customEndDate) return { startDate: null, endDate: null };
      const endDate = new Date(customEndDate);
      endDate.setHours(23, 59, 59, 999);
      // If both dates are in the past, don't fetch
      if (endDate < today) return { startDate: null, endDate: null };
      return { startDate: todayISO, endDate: endDate.toISOString() };
    }
    // week or month: today to period end
    if (dashboardDateRange?.endDate) {
      return { startDate: todayISO, endDate: dashboardDateRange.endDate };
    }
    return dashboardDateRange;
  };

  const upcomingDateRange = getUpcomingDateRange();

  const isInitialCall = useRef(true);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedDoctorState, setSelectedDoctorState] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchError, setSearchError] = useState('');
  const dropdownRef = useRef(null);
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 1024);

  // Resolve whose dashboard to show: a doctor sees their own; an admin sees the
  // doctor picked in the dropdown. The server actually re-derives a doctor's own
  // id from the JWT — docId here only matters for the admin-viewing case.
  const dashboardDocId = userRole === 'admin'
    ? (selectedDoctor?.doctorId || null)
    : localStorage.getItem('ID');
  const dashboardEnabled = userRole === 'admin' ? !!selectedDoctor : !!dashboardDocId;

  const dashboardParams = {
    docId: dashboardDocId,
    startDate: dashboardDateRange.startDate,
    endDate: dashboardDateRange.endDate,
    upcomingStartDate: upcomingDateRange.startDate,
    upcomingEndDate: upcomingDateRange.endDate,
    upcomingPage,
    upcomingLimit: 8,
    specialtyId: selectedSpecialtyId,
    addressTypeName: selectedAddress,
    apptStartDate: `${apptYear}-01-01T00:00:00.000Z`,
    apptEndDate: `${apptYear}-12-31T23:59:59.999Z`,
    earnStartDate: `${earnYear}-01-01T00:00:00.000Z`,
    earnEndDate: `${earnYear}-12-31T23:59:59.999Z`,
  };

  const { data: dashboardData, loading: dashboardLoading } =
    useDashboardSocket('doctor', dashboardParams, { enabled: dashboardEnabled });

  // Upcoming table is paginated; reset to page 1 whenever a filter that changes
  // the result set changes (period/custom dates/specialty/address/doctor).
  useEffect(() => {
    setUpcomingPage(1);
  }, [dashboardPeriod, customStartDate, customEndDate, selectedSpecialtyId, selectedAddress, selectedDoctorState]);

  // Validate search input
  const validateSearch = (value) => {
    // Sanitize input - remove potentially dangerous characters
    let sanitized = value.replace(/[<>]/g, '');
    
    // Check max length
    if (sanitized.length > 50) {
      return t('dashboard.search_exceed_limit', 'Search query cannot exceed 50 characters');
    }
    
    // Check for whitespace-only
    if (sanitized.trim() === '' && value.trim() !== '') {
      return t('dashboard.search_only_spaces', 'Search query cannot contain only spaces');
    }
    
    return '';
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    const value = e.target.value;
    
    // Sanitize input
    let sanitized = value.replace(/[<>]/g, '');
    
    // Enforce max length
    if (sanitized.length > 50) {
      sanitized = sanitized.substring(0, 50);
    }
    
    setSearchQuery(sanitized);
    
    // Validate and set error
    const error = validateSearch(sanitized);
    setSearchError(error);
  };

  // Debounce search
  const debouncedSearch = useDebounce(searchQuery.trim(), 400);

  // Fetch doctors on mount (admin only)
  useEffect(() => {
    const handleResize = () => setIsSmallScreen(window.innerWidth < 1024);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (isInitialCall.current) {
      if (userRole === 'admin') {
        dispatch(clearSelectedDoctor());
        dispatch(getDoctorNameList()); // Initial load
      } else if (userRole === 'doctor') {
        const doctorId = localStorage.getItem('ID');
        dispatch(getDoctorById(doctorId));
      }
      isInitialCall.current = false;
    }
  }, [dispatch, userRole]);

  // Fetch doctor details when selected
  useEffect(() => {
    if (selectedDoctorState && userRole === 'admin') {
      dispatch(getDoctorById(selectedDoctorState.doctorId));
    }
  }, [dispatch, selectedDoctorState]);

  // Search doctors on debounced query (only if no error)
  useEffect(() => {
    if (userRole === 'admin' && isOpen && !searchError) {
      dispatch(getDoctorNameList(debouncedSearch));
    }
  }, [debouncedSearch, dispatch, userRole, isOpen, searchError]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchQuery(''); // Reset search when closed
        setSearchError('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleReset = () => {
    setSelectedDoctorState(null);
    setIsOpen(false);
    setSearchQuery('');
    setSearchError('');
    setSelectedSpecialtyId(null);
    setSelectedAddress(null);
    dispatch(clearSelectedDoctor());
  };

  const handleSelectDoctor = (doctor) => {
    setSelectedDoctorState(doctor);
    setIsOpen(false);
    setSearchQuery('');
    setSearchError('');
    setDashboardPeriod('week');
    setCustomStartDate(null);
    setCustomEndDate(null);
    // Reset filters — option lists differ per doctor
    setSelectedSpecialtyId(null);
    setSelectedAddress(null);
    if (doctor) {
      dispatch(getDoctorById(doctor.doctorId));
    }
  };

  // Loading full page
  if (loading && !selectedDoctor) {
    return (
      <div data-testid="doctor-dashboard-loading" className="bg-white rounded-2xl p-6 w-full h-full flex items-center justify-center shadow-lg">
        <div className="flex flex-col items-center justify-center gap-4 py-6">
          <CommonLoader />
          <span data-testid="doctor-dashboard-loading-text" className="text-xl text-[#4ABDBD] font-semibold">{t('dashboard.loading')}</span>
        </div>
      </div>
    );
  }

  // Error handling
  if (error) {
    return <ErrorPage error={error} pageName={t('dashboard.doctor_dashboard')} />;
  }

  const periodSelector = (
    <div data-testid="doctor-dashboard-period-selector" className="flex flex-wrap items-center gap-3">
      <div data-testid="doctor-dashboard-period-tabs" className="flex gap-1 bg-white p-1 rounded-2xl shadow border border-gray-200">
        {[
          { id: 'today', label: t('dashboard.time_ranges.today', 'Today'), icon: Clock },
          { id: 'week', label: t('dashboard.time_ranges.week', 'Week'), icon: CalendarDays },
          { id: 'month', label: t('dashboard.time_ranges.month', 'Month'), icon: CalendarRange },
          { id: 'custom', label: t('dashboard.time_ranges.custom', 'Custom'), icon: Settings2 },
        ].map((tab) => (
          <button
            key={tab.id}
            data-testid={`doctor-dashboard-period-tab-${tab.id}`}
            onClick={() => handlePeriodChange(tab.id)}
            className={`relative px-4 py-2 text-sm font-semibold rounded-xl transition-all duration-300 z-10 flex items-center gap-1.5 ${dashboardPeriod === tab.id ? 'text-white' : 'text-gray-600 hover:text-[#4ABDBD] cursor-pointer'}`}
          >
            {dashboardPeriod === tab.id && (
              <motion.div
                data-testid={`doctor-dashboard-period-tab-${tab.id}-active`}
                layoutId="activeDashboardPeriod"
                className="absolute inset-0 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] rounded-xl shadow-lg !m-0"
                transition={{ type: 'spring', bounce: 0.2, duration: 0.6 }}
              />
            )}
            <tab.icon data-testid={`doctor-dashboard-period-tab-${tab.id}-icon`} className="w-4 h-4 relative z-20" />
            <span data-testid={`doctor-dashboard-period-tab-${tab.id}-label`} className="relative z-20">{tab.label}</span>
          </button>
        ))}
      </div>
      {dashboardPeriod === 'custom' && (
        <div data-testid="doctor-dashboard-custom-date-range" className="flex items-center gap-2">
          <CustomDatePicker
            value={customStartDate || ''}
            onChange={(e) => setCustomStartDate(e.target.value)}
            maxDate={customEndDate || null}
            placeholder={t('dashboard.from_date', 'From')}
          />
          <span data-testid="doctor-dashboard-custom-date-separator" className="text-gray-400 text-sm">-</span>
          <CustomDatePicker
            value={customEndDate || ''}
            onChange={(e) => setCustomEndDate(e.target.value)}
            minDate={customStartDate || null}
            placeholder={t('dashboard.to_date', 'To')}
          />
        </div>
      )}
    </div>
  );

  return (
    <motion.div
      data-testid="doctor-dashboard"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className={`${window.innerWidth <= 1024 ? 'p-4' : 'p-0'}`}
    >
      {/* Header: title + (admin: doctor dropdown, doctor: period selector) */}
      <div data-testid="doctor-dashboard-header" className="mb-4 flex flex-wrap justify-between items-center gap-3">
        <h1 data-testid="doctor-dashboard-heading" className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent">
          {userRole === 'admin'
            ? t('dashboard.doctor_dashboard', 'Doctor Dashboard')
            : t('dashboard.my_dashboard', 'My Dashboard')}
        </h1>
        {userRole === 'admin' ? (
          <div ref={dropdownRef} data-testid="doctor-dropdown" className={`relative ${isSmallScreen ? 'w-full' : 'w-64'}`}>
            {/* Toggle Button */}
            <button
              data-testid="doctor-dropdown-toggle"
              onClick={() => !loadingList && setIsOpen(!isOpen)}
              disabled={loadingList}
              className={`w-full px-4 py-2 border rounded-xl flex justify-between items-center font-medium transition
                ${loadingList
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'text-white bg-gradient-to-br from-[#4ABDBD] to-[#3A9B9B] cursor-pointer'
                }
                ${isSmallScreen ? 'text-sm' : ''}
              `}
            >
              <span data-testid="selected-doctor-display" className="truncate w-[85%] text-left">
                {selectedDoctorState
                  ? selectedDoctorState.name
                  : t('dashboard.select_doctor')}
              </span>
              <span data-testid="dropdown-arrow" className="ml-2 flex items-center">
                {loadingList ? (
                  <span className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                ) : isOpen ? (
                  <ChevronUp />
                ) : (
                  <ChevronDown />
                )}
              </span>
            </button>

            {/* Dropdown Menu */}
            {isOpen && (
              <div
                data-testid="doctor-dropdown-list"
                className={`absolute left-0 mt-2 w-full z-50 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden
                  ${isSmallScreen ? 'text-sm' : ''}
                `}
              >
                {/* Search Input */}
                <div data-testid="doctor-dropdown-search-container" className="p-2 border-b border-gray-200">
                  <div className="relative">
                    <Search data-testid="doctor-dropdown-search-icon" className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                      data-testid="doctor-dropdown-search-input"
                      type="text"
                      value={searchQuery}
                      onChange={handleSearchChange}
                      placeholder={t('dashboard.search_doctor')}
                      maxLength={50}
                      className={`w-full pl-10 pr-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 ${
                        searchError
                          ? 'border-red-300 bg-red-50 focus:ring-red-500'
                          : 'border-gray-300 focus:ring-[#4ABDBD]'
                      }`}
                      autoFocus
                    />
                  </div>
                  {searchError && (
                    <p data-testid="doctor-dropdown-search-error" className="text-xs text-red-600 mt-1 px-1">{searchError}</p>
                  )}
                </div>

                {/* Loading State */}
                {loadingList && (
                  <div data-testid="doctor-dropdown-list-loading" className="p-4 flex justify-center">
                    <span className="h-5 w-5 border-2 border-[#4ABDBD] border-t-transparent rounded-full animate-spin"></span>
                  </div>
                )}

                {/* Doctor List */}
                {!loadingList && (
                  <ul data-testid="doctor-dropdown-list-ul" className="max-h-60 overflow-y-auto custom-scrollbar">
                    {/* Reset Option */}
                    {/* <li
                      data-testid="reset-doctor-selection"
                      onClick={handleReset}
                      className="px-4 py-2.5 cursor-pointer text-gray-500 font-medium hover:bg-[#4ABDBD] hover:text-white transition border-b border-gray-100"
                    >
                      {t('dashboard.select_doctor')}
                    </li> */}

                    {/* No Results */}
                    {doctorNameList.length === 0 ? (
                      <li data-testid="doctor-dropdown-empty-text" className="px-4 py-3 text-gray-500 text-center">
                        {searchQuery ? t('dashboard.no_doctors_found') : t('dashboard.no_doctors_available')}
                      </li>
                    ) : (
                      doctorNameList.map((doctor, idx) => (
                        <li
                          key={doctor.doctorId}
                          data-testid={`doctor-option-${doctor.doctorId}`}
                          onClick={() => doctor.available && handleSelectDoctor(doctor)}
                          className={`px-4 py-2.5 transition flex justify-between items-center
                            ${idx !== doctorNameList.length - 1 ? 'border-b border-gray-100' : ''}
                            ${doctor.available
                              ? 'cursor-pointer hover:bg-[#4ABDBD] hover:text-white'
                              : 'cursor-not-allowed opacity-50 bg-gray-50'
                            }`}
                        >
                          <span data-testid={`doctor-option-name-${doctor.doctorId}`}>{doctor.name}</span>
                          {!doctor.available && (
                            <span data-testid={`doctor-option-unavailable-${doctor.doctorId}`} className="text-xs text-red-500">({t('dashboard.unavailable')})</span>
                          )}
                        </li>
                      ))
                    )}
                  </ul>
                )}
              </div>
            )}
          </div>
        ) : null}
      </div>

      {/* Dashboard Content */}
      {userRole === 'admin' && !selectedDoctor ? (
        <div data-testid="doctor-dashboard-no-doctor-state" className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="mb-6">
              <div className="w-24 h-24 mx-auto mb-4 bg-gradient-to-br from-[#4ABDBD]/20 to-[#3A9B9B]/20 rounded-full flex items-center justify-center">
                <svg data-testid="doctor-dashboard-no-doctor-icon" className="w-12 h-12 text-[#4ABDBD]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
            </div>
            <h3 data-testid="doctor-dashboard-no-doctor-title" className="text-2xl font-bold text-gray-800 mb-2">
              {t('dashboard.select_doctor_title')}
            </h3>
            <p data-testid="doctor-dashboard-no-doctor-message" className="text-gray-600 text-lg mb-6">
              {t('dashboard.select_doctor_message')}
            </p>
            <div data-testid="doctor-dashboard-no-doctor-instruction" className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-[#4ABDBD]/10 to-[#3A9B9B]/10 rounded-lg border border-[#4ABDBD]/20">
              <svg data-testid="doctor-dashboard-no-doctor-info-icon" className="w-5 h-5 text-[#4ABDBD] mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span data-testid="doctor-dashboard-no-doctor-instruction-text" className="text-[#4ABDBD] font-medium">{t('dashboard.select_doctor_instruction')}</span>
            </div>
          </div>
        </div>
      ) : (
        <div data-testid="dashboard-content" className="grid w-full max-w-full gap-4">
          {/* Controls bar: timeframe + specialty + address filters (impact whole dashboard) */}
          <div data-testid="dashboard-controls" className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
            {periodSelector}
            <DashboardFilters
              filterOptions={dashboardData?.filters}
              specialtyId={selectedSpecialtyId}
              addressTypeName={selectedAddress}
              onSpecialtyChange={setSelectedSpecialtyId}
              onAddressChange={setSelectedAddress}
            />
          </div>

          <WelcomeSection
            booking={dashboardData?.booking}
            loading={dashboardLoading}
            dateRange={dashboardDateRange}
            selectedPeriod={dashboardPeriod}
          />

          {/* KPI cards (upcoming, revenue, no-show €, regular %, hourly rate, inactive, new patients, occupation) */}
          <KpiCards
            cardSummary={dashboardData?.cardSummary}
            loading={dashboardLoading}
            selectedPeriod={dashboardPeriod}
          />

          {/* Upcoming appointments table (Date/time, Patient, Specialty, Type, Price) */}
          <UpcomingAppointmentsTable
            upcomingFlat={dashboardData?.upcoming}
            loading={dashboardLoading}
            page={upcomingPage}
            onPageChange={setUpcomingPage}
          />

          <SpecialtyTrendGraph
            trendOverTime={dashboardData?.trend}
            loading={dashboardLoading}
          />
          <div className="grid grid-cols-1 w-full gap-4">
            <OccupancyByDay
              occupancyByDay={dashboardData?.occupancy}
              loading={dashboardLoading}
            />
            <SpecialtyTypeBreakdown
              specialtyTypeBreakdown={dashboardData?.specialtyBreakdown}
              loading={dashboardLoading}
            />
          </div>

          {/* Analytics — Appointments / Revenue (tabbed in one card) */}
          <DashboardTabCard
            title={t('dashboard.by_specialty', 'By Specialty')}
            tabs={[
              {
                id: 'appointments',
                label: t('dashboard.appointments_tab', 'Appointments'),
                render: () => (
                  <AppointmentAnalytics embedded appointmentAnalytics={dashboardData?.earningAnalytics} loading={dashboardLoading} />
                ),
              },
              {
                id: 'revenue',
                label: t('dashboard.revenue_tab', 'Revenue'),
                render: () => (
                  <EarningsAnalytics embedded earningsAnalytics={dashboardData?.earningAnalytics} loading={dashboardLoading} />
                ),
              },
            ]}
          />

          <NoShowAnalytics
            noShowAnalytics={dashboardData?.noShow}
            loading={dashboardLoading}
          />

          {/* Month-wise — Appointments / Revenue (tabbed in one card) */}
          <DashboardTabCard
            title={t('dashboard.monthly_trend', 'Monthly Trend')}
            tabs={[
              {
                id: 'appointments',
                label: t('dashboard.appointments_tab', 'Appointments'),
                render: () => (
                  <AppointmentYearGraph embedded monthWiseAppointments={dashboardData?.monthWiseAppointments} loading={dashboardLoading} selectedYear={apptYear} onYearChange={setApptYear} />
                ),
              },
              {
                id: 'revenue',
                label: t('dashboard.revenue_tab', 'Revenue'),
                render: () => (
                  <EarnYearGraph embedded monthWiseEarning={dashboardData?.monthWiseEarning} loading={dashboardLoading} selectedYear={earnYear} onYearChange={setEarnYear} />
                ),
              },
            ]}
          />
        </div>
      )}
    </motion.div>
  );
}

export default DoctorDashboard;